import React, { useEffect, useState, useCallback } from "react";
import { useOutletContext, useNavigate } from "react-router-dom";
import { Header, roleCanAccess } from "../components/Sidebar";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { GROUP_META, STATUS_META, ROLE_LABELS, formatGs, pyToday } from "../lib/format";
import { CodeList } from "../components/EnvioDetalle";
import {
  Package, Truck, ClipboardList, Building2, Users2, ShieldCheck,
  BadgePercent, Wallet, SearchCheck, ArrowRight, Store, Bike, Car,
  Coins, FileText, CreditCard, BadgeDollarSign, PlusCircle, PackageOpen,
  AlertTriangle, BarChart3, Settings, UserRound, KeyRound, Route,
} from "lucide-react";

const STAFF = ["admin", "encargado", "encargado_comisionista"];
const OPS = ["admin", "encargado", "encargado_comisionista", "punto"];

const MODULES = [
  { to: "/app/envios", title: "Envíos", desc: "Boletas, carga masiva y edición", icon: Package, roles: ["admin", "encargado", "encargado_comisionista", "punto", "cliente"] },
  { to: "/app/mi-ruta", title: "Mi Ruta", desc: "Tus retiros y entregas asignadas", icon: Route, roles: ["admin", "encargado", "encargado_comisionista", "repartidor", "comisionista"] },
  { to: "/app/retiros", title: "Retiros", desc: "Solicitar y asignar retiros", icon: Truck, roles: ["admin", "encargado", "encargado_comisionista", "punto", "cliente"] },
  { to: "/app/tracking", title: "Tracking", desc: "Consultar estado de un envío", icon: SearchCheck, roles: "*" },
  { to: "/app/usuarios", title: "Usuarios y Roles", desc: "Gestión de accesos (RBAC)", icon: ShieldCheck, roles: ["admin"] },
  { to: "/app/aprobaciones", title: "Aprobaciones", desc: "Validar nuevos registros", icon: UserRound, roles: ["admin"] },
  { to: "/app/clientes", title: "Clientes", desc: "Remitentes y cuentas", icon: Users2, roles: OPS },
  { to: "/app/solicitudes", title: "Solicitudes", desc: "Retiros y envíos pendientes", icon: ClipboardList, roles: OPS },
  { to: "/app/sucursales", title: "Sucursales", desc: "5 sucursales del sistema", icon: Building2, roles: STAFF },
  { to: "/app/gestionar-sucursal", title: "Gestionar sucursal", desc: "Operativa por sucursal", icon: Store, roles: OPS },
  { to: "/app/repartidores", title: "Repartidores", desc: "Equipo de reparto", icon: Bike, roles: STAFF },
  { to: "/app/vehiculos", title: "Vehículos", desc: "Flota de reparto", icon: Car, roles: STAFF },
  { to: "/app/cod", title: "COD", desc: "Cobros contra entrega", icon: Coins, roles: STAFF },
  { to: "/app/liquidaciones", title: "Liquidaciones", desc: "Pago COD a remitentes", icon: FileText, roles: STAFF },
  { to: "/app/rendiciones", title: "Rendiciones", desc: "Monto a rendir y cierres", icon: Wallet, roles: ["admin", "encargado", "encargado_comisionista", "repartidor", "comisionista", "punto"] },
  { to: "/app/finanzas", title: "Finanzas", desc: "Ingresos, cobros y KPIs", icon: BadgePercent, roles: STAFF },
  { to: "/app/credito", title: "Estado de Cuenta", desc: "Liquidaciones y crédito por remitente", icon: CreditCard, roles: STAFF },
  { to: "/app/comisiones", title: "Comisiones y pagos", desc: "Comisión sobre flete", icon: BadgeDollarSign, roles: ["admin", "encargado", "encargado_comisionista", "comisionista"] },
  { to: "/app/boleta-ocasional", title: "Crear boleta", desc: "Nueva boleta y retiro directo", icon: PlusCircle, roles: ["admin", "encargado", "encargado_comisionista", "punto", "repartidor", "comisionista"] },
  { to: "/app/almacenamiento", title: "Almacenamiento", desc: "Paquetes en depósito", icon: PackageOpen, roles: OPS },
  { to: "/app/incidencias", title: "Incidencias", desc: "Envíos con problemas", icon: AlertTriangle, roles: OPS },
  { to: "/app/documentos", title: "Documentos", desc: "Comprobantes firmados", icon: FileText, roles: OPS },
  { to: "/app/terminos", title: "Términos y Condiciones", desc: "Condiciones del servicio", icon: ShieldCheck, roles: ["cliente"] },
  { to: "/app/reportes", title: "Reportes", desc: "Exportar CSV por período", icon: BarChart3, roles: STAFF },
  { to: "/app/configuracion", title: "Configuración", desc: "Parámetros del sistema", icon: Settings, roles: ["admin"] },
  { to: "/app/integraciones", title: "API / Integraciones", desc: "Conectá tu tienda online", icon: KeyRound, roles: ["admin"] },
  { to: "/app/perfil", title: "Mi perfil", desc: "Datos de tu cuenta", icon: UserRound, roles: "*" },
];

export default function Dashboard() {
  const { setMobileOpen } = useOutletContext();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [sucTareas, setSucTareas] = useState(null);
  const [rutaEnvios, setRutaEnvios] = useState([]);
  const [rutaRetiros, setRutaRetiros] = useState([]);
  const [todos, setTodos] = useState([]);
  const [grupoSel, setGrupoSel] = useState(null);
  const [celdaSel, setCeldaSel] = useState(null);
  const [cuenta, setCuenta] = useState(null);
  const isAdmin = user?.role === "admin";
  const isDelivery = ["repartidor", "comisionista"].includes(user?.role);

  const load = useCallback(() => {
    api.get("/dashboard/stats").then(({ data }) => setStats(data)).catch(() => {});
    if (user?.role === "admin") api.get("/dashboard/sucursales-tareas").then(({ data }) => setSucTareas(data)).catch(() => {});
    if (user?.role === "cliente") api.get("/mi-cuenta").then(({ data }) => setCuenta(data)).catch(() => {});
    if (user && !["repartidor", "comisionista"].includes(user.role)) api.get("/envios").then(({ data }) => setTodos(data)).catch(() => {});
    if (["repartidor", "comisionista"].includes(user?.role)) {
      api.get("/envios").then(({ data }) => setRutaEnvios(data)).catch(() => {});
      api.get("/retiros").then(({ data }) => setRutaRetiros(data)).catch(() => {});
    }
  }, [user]);

  useEffect(() => {
    load();
    const onFocus = () => { if (document.visibilityState === "visible") load(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    const interval = setInterval(onFocus, 30000);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
      clearInterval(interval);
    };
  }, [load]);

  const modules = MODULES.filter((m) => roleCanAccess(user?.role, m.to));
  const porGrupo = {};
  todos.forEach((e) => { const g = STATUS_META[e.status]?.group || "creado"; (porGrupo[g] = porGrupo[g] || []).push(e.code); });
  const TAREA_LABEL = { retiros_asignar: "Retiros por asignar", por_recibir: "Por recibir/embarcar", desembarcados_sin_asignar: "Desembarcados sin asignar", en_reparto: "En reparto", incidencias_abiertas: "Incidencias abiertas", reprogramados_hoy: "Reprogramados hoy" };

  return (
    <>
      <Header title="Centro de Control" subtitle={`Panel · ${ROLE_LABELS[user?.role] || ""}`} onMenu={() => setMobileOpen(true)} />
      <main className="p-4 sm:p-6 lg:p-8 space-y-6">
        {/* Estados del envío (oculto para repartidores: su foco es Mi Ruta) */}
        {!["repartidor", "comisionista"].includes(user?.role) && (
        <section className="bg-white rounded-xl border border-[#E2E8F0] p-5 shadow-sm fade-up">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-[#0A1F44] font-display">Estados del envío</h2>
            <span className="text-xs text-[#64748B]">{todos.length} envíos totales · tocá un cuadro para ver los códigos</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3 mt-4">
            {Object.entries(GROUP_META).map(([key, g]) => (
              <button key={key} data-testid={`estado-pill-${key}`} onClick={() => setGrupoSel(grupoSel === key ? null : key)}
                className={`flex items-center justify-between p-3 rounded-lg border ${g.pill} transition-all hover:shadow-md ${grupoSel === key ? "ring-2 ring-offset-1 ring-[#0A1F44]" : ""}`}>
                <span className="flex items-center gap-2 text-sm font-medium text-left">
                  <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: g.dot }} />
                  {g.label}
                </span>
                <span className="text-lg font-extrabold tabular-nums">{porGrupo[key]?.length ?? 0}</span>
              </button>
            ))}
          </div>
          {grupoSel && (
            <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50/60 p-3" data-testid="estado-lista">
              <div className="text-xs font-semibold text-[#64748B] mb-2">{GROUP_META[grupoSel].label} · {porGrupo[grupoSel]?.length || 0} envío(s)</div>
              <CodeList codes={porGrupo[grupoSel]} onChanged={load} />
            </div>
          )}
        </section>
        )}

        {cuenta && (
          <section className="bg-white rounded-xl border border-[#E2E8F0] p-5 shadow-sm fade-up" data-testid="mi-cuenta-box">
            <h2 className="text-lg font-semibold text-[#0A1F44] font-display">Mi cuenta</h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-4">
              {[["Saldo de crédito", cuenta.credito_saldo, "text-rose-600", "saldo"], ["Flete a crédito", cuenta.credito_cargado, "text-[#0A1F44]", "cargado"], ["Pagos realizados", cuenta.pagos, "text-emerald-600", "pagos"], ["Productos por cobrar", cuenta.liquidacion_pendiente, "text-[#F5A623]", "liq"]].map(([l, v, c, id]) => (
                <div key={id} className="rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid={`mi-cuenta-${id}`}>
                  <div className={`text-lg font-extrabold tabular-nums ${c}`}>{formatGs(v)}</div>
                  <div className="text-[11px] text-[#64748B] font-medium">{l}</div>
                </div>
              ))}
            </div>
            <div className="mt-4 text-xs font-semibold text-[#64748B]">Movimientos</div>
            <div className="mt-1 divide-y divide-slate-100 max-h-64 overflow-y-auto" data-testid="mi-cuenta-movimientos">
              {cuenta.movimientos.map((m, i) => (
                <div key={i} className="flex items-center justify-between py-2 text-sm">
                  <div><div className="text-[#0A1F44]">{m.detalle}</div><div className="text-[11px] text-[#64748B]">{m.fecha ? new Date(m.fecha).toLocaleDateString("es-PY") : ""}</div></div>
                  <b className={m.tipo === "pago" ? "text-emerald-600" : "text-rose-600"}>{m.tipo === "pago" ? "−" : "+"}{formatGs(m.monto)}</b>
                </div>
              ))}
              {cuenta.movimientos.length === 0 && <div className="py-4 text-sm text-[#64748B]">Sin movimientos de crédito.</div>}
            </div>
          </section>
        )}

        {/* Resumen de ruta del repartidor/comisionista, visible en la pantalla principal */}
        {isDelivery && (() => {
          const uid = user?.id;
          const hoy = pyToday();
          const tiles = [
            { id: "retiros", label: "Retiros asignados", n: rutaRetiros.filter((r) => r.status !== "completado" && r.repartidor_id === uid).length },
            { id: "por-retirar", label: "Por retirar", n: rutaEnvios.filter((e) => e.repartidor_retiro_id === uid && e.status === "solicitado").length },
            { id: "por-depositar", label: "Por depositar", n: rutaEnvios.filter((e) => e.repartidor_retiro_id === uid && e.status === "retirado").length },
            { id: "en-reparto", label: "En reparto", n: rutaEnvios.filter((e) => e.repartidor_id === uid && ["asignado_repartidor", "en_reparto"].includes(e.status)).length },
            { id: "entregadas", label: "Entregadas hoy", n: rutaEnvios.filter((e) => e.status === "entregado" && (e.entregado_at || "").slice(0, 10) === hoy).length },
            { id: "incidencias", label: "Incidencias", n: rutaEnvios.filter((e) => e.status === "incidente").length },
          ];
          return (
            <section className="bg-white rounded-xl border border-[#E2E8F0] p-5 shadow-sm fade-up" data-testid="ruta-resumen-box">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-[#0A1F44] font-display">Mi ruta de hoy</h2>
                <button onClick={() => navigate("/app/mi-ruta")} data-testid="ruta-ir-btn" className="text-sm font-semibold text-[#3B82F6] hover:underline flex items-center gap-1">Ir a Mi Ruta <ArrowRight className="h-3.5 w-3.5" /></button>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mt-4">
                {tiles.map((t) => (
                  <div key={t.id} data-testid={`ruta-tile-${t.id}`} className={`p-3 rounded-lg border text-center ${t.n > 0 ? "bg-blue-50/60 border-blue-200" : "bg-slate-50 border-slate-200"}`}>
                    <div className={`text-xl font-extrabold tabular-nums ${t.n > 0 ? "text-[#0A1F44]" : "text-slate-300"}`}>{t.n}</div>
                    <div className="text-[11px] text-[#64748B] font-medium leading-tight mt-0.5">{t.label}</div>
                  </div>
                ))}
              </div>
            </section>
          );
        })()}

        {/* Monto a rendir (repartidor/punto/comisionista) */}
        {stats?.mi_monto_a_rendir > 0 && (
          <section className="bg-white rounded-xl border border-[#E2E8F0] p-5 shadow-sm flex items-center justify-between fade-up">
            <div className="flex items-center gap-3">
              <div className="h-11 w-11 rounded-xl bg-amber-50 border border-amber-200 flex items-center justify-center">
                <Wallet className="h-5 w-5 text-[#F5A623]" />
              </div>
              <div>
                <div className="text-xs text-[#64748B] font-medium uppercase tracking-wide">Tu monto a rendir</div>
                <div className="text-2xl font-extrabold text-[#0A1F44]">{formatGs(stats.mi_monto_a_rendir)}</div>
              </div>
            </div>
            <button onClick={() => navigate("/app/rendiciones")} className="text-sm font-semibold text-[#3B82F6] hover:underline">Ver detalle</button>
          </section>
        )}

        {/* Mini-tablero: tareas del día por sucursal (solo admin) */}
        {isAdmin && sucTareas && sucTareas.length > 0 && (
          <section className="bg-white rounded-xl border border-[#E2E8F0] p-5 shadow-sm fade-up" data-testid="mini-tablero-sucursales">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-[#0A1F44] font-display">Tareas del día por sucursal</h2>
              <span className="text-xs text-[#64748B]">{sucTareas.length} sucursales</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-[#64748B] text-xs uppercase">
                  <tr className="border-b border-slate-100">
                    <th className="text-left font-semibold px-3 py-2">Sucursal</th>
                    <th className="text-center font-semibold px-2 py-2">Retiros<br/>x asignar</th>
                    <th className="text-center font-semibold px-2 py-2">Por recibir/<br/>embarcar</th>
                    <th className="text-center font-semibold px-2 py-2">Desemb. sin<br/>asignar</th>
                    <th className="text-center font-semibold px-2 py-2">En<br/>reparto</th>
                    <th className="text-center font-semibold px-2 py-2">Incid.<br/>abiertas</th>
                    <th className="text-center font-semibold px-2 py-2">Reprog.<br/>hoy</th>
                    <th className="text-center font-semibold px-2 py-2">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {[...sucTareas].sort((a, b) => b.tareas.total - a.tareas.total).map((s) => {
                    const t = s.tareas;
                    const cell = (key, warn) => { const n = t[key]; return <td className={`text-center px-2 py-2.5 tabular-nums font-semibold ${n > 0 ? (warn ? "text-rose-600" : "text-[#0A1F44]") : "text-slate-300"}`}><button disabled={!n} data-testid={`tablero-${s.code}-${key}`} onClick={(ev) => { ev.stopPropagation(); setCeldaSel({ suc: s.code, key, items: s.listas?.[key] || [] }); }} className={n ? "underline decoration-dotted underline-offset-4 hover:text-[#3B82F6]" : ""}>{n}</button></td>; };
                    return (
                      <tr key={s.code} data-testid={`tablero-row-${s.code}`} className={`hover:bg-slate-50/60 ${t.total === 0 ? "opacity-60" : ""}`}>
                        <td className="px-3 py-2.5">
                          <div className="flex items-center gap-2">
                            <span className="font-mono font-bold text-xs px-2 py-0.5 rounded bg-blue-50 text-[#0A1F44] border border-blue-100">{s.code}</span>
                            <span className="text-[#0A1F44] font-medium hidden sm:inline">{s.name}</span>
                            {s.es_punto && <span className="text-[10px] font-bold text-[#F5A623]">PUNTO</span>}
                            {s.activa === false && <span className="text-[10px] font-bold text-rose-500">INACTIVA</span>}
                          </div>
                        </td>
                        {cell("retiros_asignar", true)}
                        {cell("por_recibir")}
                        {cell("desembarcados_sin_asignar", true)}
                        {cell("en_reparto")}
                        {cell("incidencias_abiertas", true)}
                        {cell("reprogramados_hoy", true)}
                        <td className="text-center px-2 py-2.5"><span className={`inline-block min-w-7 px-2 py-0.5 rounded-full text-xs font-extrabold ${t.total > 0 ? "bg-[#0A1F44] text-white" : "bg-slate-100 text-slate-400"}`}>{t.total}</span></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {celdaSel && (
              <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50/60 p-3" data-testid="tablero-lista">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs font-semibold text-[#64748B]">{celdaSel.suc} · {TAREA_LABEL[celdaSel.key]} · {celdaSel.items.length}</div>
                  <button onClick={() => setCeldaSel(null)} data-testid="tablero-lista-cerrar" className="text-xs font-semibold text-[#3B82F6]">Cerrar</button>
                </div>
                {celdaSel.key === "retiros_asignar"
                  ? <div className="flex flex-wrap gap-1.5">{celdaSel.items.map((n, i) => <span key={i} className="text-xs px-2 py-0.5 rounded bg-white border border-slate-200">{n}</span>)}</div>
                  : <CodeList codes={celdaSel.items} onChanged={load} />}
              </div>
            )}
          </section>
        )}

        {/* Módulos */}
        <section>
          <h2 className="text-lg font-semibold text-[#0A1F44] font-display mb-3">Módulos</h2>
          <div className="grid grid-cols-2 gap-3 sm:gap-4">
            {modules.map((m) => {
              const Icon = m.icon;
              return (
                <button key={m.to} onClick={() => navigate(m.to)} data-testid={`module-card-${m.to.split("/").pop()}`}
                  className="text-left bg-white rounded-xl border border-[#E2E8F0] p-4 sm:p-5 shadow-sm hover:border-[#3B82F6]/50 hover:shadow-md transition-all duration-200 group">
                  <div className="h-11 w-11 sm:h-12 sm:w-12 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center group-hover:bg-blue-100 transition-colors">
                    <Icon className="h-5 w-5 sm:h-6 sm:w-6 text-[#3B82F6]" />
                  </div>
                  <div className="mt-3 font-bold text-[#0A1F44] text-sm sm:text-base leading-tight">{m.title}</div>
                  <div className="text-xs text-[#64748B] mt-0.5 hidden sm:block">{m.desc}</div>
                </button>
              );
            })}
          </div>
        </section>
      </main>
    </>
  );
}
